import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
    BotMessageSquare,
    TestTubeDiagonal,
    Bot
} from 'lucide-react';
import {ReactComponent as BackArrow} from "../svg/back.svg";
import {useDataContext} from "./SurveyContextProvider";
import {MessageEditor} from "./SurveyMessages";
import '../scss/SurveyBots.scss';
import {useSupportBotContext} from "./SupportBotEditor/SupportBotData";
import {GetAssistant} from "../api/APIBot";
import WhatsappMessage from "./surveyEditor/messagesTab/WhatsappMessage";
import {BotCard} from "../components/BotCard/BotCard";

const SurveyBots = ({ useWhatsapp = false }) => {
    const intl = useIntl();
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData');
    const { value: supportBots, setValue: setSupportBots } = useDataContext('supportBots');

    const data = surveyData?.config;

    // Initialize selected bots from surveyData or empty array if it doesn't exist
    const [selectedBots, setSelectedBots] = useState(Array.isArray(data?.supportBots) ? data?.supportBots : []);
    const [expandedBots, setExpandedBots] = useState({});
    const [useCustomInstructions, setUseCustomInstructions] = useState(data?.useCustomInstructions || false)
    const [customInstructions, setCustomInstructions] = useState(data?.customInstructions || "")

    const placeholderText = intl.formatMessage({
        id: 'bot.message.placeholder.first',
        defaultMessage: 'This is the first message the support bot sends to students. Click to type, and maybe start with a greeting? :)',
    });

    const customInstructionsPlaceholder = intl.formatMessage({
        id: 'bot.customInstructions.placeholder',
        defaultMessage: 'Write custom instructions for the bot. This will bypass the structured bot modules.',
    });

    // Update surveyData when selectedBots changes
    useEffect(() => {
        if (surveyData?.config) {
            setSurveyData({
                ...surveyData,
                config: {
                    ...surveyData.config,
                    supportBots: selectedBots
                }
            });
        }
    }, [selectedBots]);

    // Group bots by version group to handle version selection
    const botsByVersionGroup = React.useMemo(() => {
        if (!supportBots) return {};

        const grouped = {};

        supportBots.forEach(bot => {
            const versionGroup = bot.config?.versionGroup;
            const version = bot.config?.version;

            if (versionGroup) {
                if (!grouped[versionGroup]) {
                    grouped[versionGroup] = [];
                }
                grouped[versionGroup].push(bot);
            }
        });

        return grouped;
    }, [supportBots]);

    const toggleBotSelection = (botId) => {
        if (selectedBots.includes(botId)) {
            setSelectedBots(selectedBots.filter(id => id !== botId));
        } else {
            setSelectedBots([...selectedBots, botId]);
        }
    };

    const handleToggleExpanded = (botId, groupId, isExpanded) => {
        const toggleId = groupId || botId;  // Use versionGroup if available
        setExpandedBots(prev => ({
            ...prev,
            [toggleId]: isExpanded
        }));
    };

    // Track current visible version for each group (not just selected ones)
    const [visibleVersions, setVisibleVersions] = useState({});

    // When a version changes, update which bot is displayed for that group
    const handleVersionChange = (groupId, newVersion, botId) => {
        // Find the bot with the selected version in the same group
        const botsInGroup = botsByVersionGroup[groupId] || [];
        const selectedBot = botsInGroup.find(bot => bot.config?.version === newVersion);

        if (selectedBot) {
            // Update the visible version for this group
            setVisibleVersions(prev => ({
                ...prev,
                [groupId]: selectedBot.id
            }));

            // Find if any bot from this group is currently selected
            const currentSelectedBotIdFromGroup = selectedBots.find(botId => {
                const bot = supportBots.find(b => b.id === botId);
                return bot?.config?.versionGroup === groupId;
            });

            if (currentSelectedBotIdFromGroup) {
                // Replace the currently selected bot with the new version
                setSelectedBots(prev =>
                    prev.map(id =>
                        id === currentSelectedBotIdFromGroup ? selectedBot.id : id
                    )
                );
            }
        }
    };

    // Filter bots to show only one bot per version group
    const filteredBots = React.useMemo(() => {
        if (!supportBots) return [];

        const processedGroups = new Set();
        const botsToShow = [];

        supportBots.forEach(bot => {
            // Skip draft bots
            if (bot?.status === 'DRAFT') {
                return;
            }

            const versionGroup = bot.config?.versionGroup;

            // If this bot has no version group, include it directly
            if (!versionGroup) {
                botsToShow.push(bot);
                return;
            }

            // If we've already processed this version group, skip
            if (processedGroups.has(versionGroup)) {
                return;
            }

            // Mark this version group as processed
            processedGroups.add(versionGroup);

            // Determine which bot from this group should be shown
            let botToShow = bot; // Default to the current bot

            // Check if there's a selected bot from this group
            const selectedBotIdFromGroup = selectedBots.find(botId => {
                const b = supportBots.find(sb => sb.id === botId);
                return b?.config?.versionGroup === versionGroup && b?.status !== 'DRAFT';
            });

            if (selectedBotIdFromGroup) {
                // Show the selected version
                const selectedBot = supportBots.find(b => b.id === selectedBotIdFromGroup);
                if (selectedBot && selectedBot?.status !== 'DRAFT') {
                    botToShow = selectedBot;
                }
            } else if (visibleVersions[versionGroup]) {
                // Show the visible version set by version selector
                const visibleBot = supportBots.find(b => b.id === visibleVersions[versionGroup]);
                if (visibleBot && visibleBot?.status !== 'DRAFT') {
                    botToShow = visibleBot;
                }
            }

            // If the current botToShow is draft, find first non-draft version in group
            if (botToShow?.status === 'DRAFT') {
                const nonDraftBot = botsByVersionGroup[versionGroup]?.find(b => b?.status !== 'DRAFT');
                if (nonDraftBot) {
                    botToShow = nonDraftBot;
                } else {
                    // All versions are draft, skip this group entirely
                    return;
                }
            }

            botsToShow.push(botToShow);
        });

        // Sort by latest update (newest first) - matching SupportBots behavior
        botsToShow.sort((a, b) => {
            const dateA = new Date(a.updated);
            const dateB = new Date(b.updated);
            return dateB - dateA;
        });

        return botsToShow;
    }, [supportBots, selectedBots, visibleVersions]);

    const handleToggleCustomInstructions = () => {
        const newUseCustomInstructions = !useCustomInstructions;
        setUseCustomInstructions(newUseCustomInstructions)

        if (surveyData?.config) {
            setSurveyData({
                ...surveyData,
                config: {
                    ...surveyData.config,
                    useCustomInstructions: newUseCustomInstructions
                }
            });
        }
    };

    const handleCustomInstructionsChange = (text) => {
        setCustomInstructions(text)

        if (surveyData?.config) {
            setSurveyData({
                ...surveyData,
                config: {
                    ...surveyData.config,
                    customInstructions: text
                }
            });
        }
    };

    return (
        <>
            <h4>
                <BotMessageSquare size={20}/>
                {intl.formatMessage({id:'survey.firstMessage',defaultMessage:'First message'})}
            </h4>
            <div className={"bot-settings"}>
                <div className={"node-content"}>
                    {useWhatsapp ?
                        <WhatsappMessage /> :
                        <MessageEditor
                            text={data?.message}
                            keyName={"initial"}
                            placeholder={placeholderText}
                        />
                    }
                </div>
                <div className={"sub-group"}>
                    {
                        !useCustomInstructions ?
                            <>
                                <div className={"section-header"}>
                                    <h4>
                                        <Bot size={18} />
                                        {intl.formatMessage({
                                            id: 'selectSupportBots',
                                            defaultMessage: 'Select Support Bots',
                                        })} ({selectedBots?.length})
                                    </h4>
                                </div>
                                <div className="support-bots">
                                    {filteredBots?.length > 0 ?
                                        filteredBots.map(bot => {
                                            const isSelected = selectedBots.includes(bot.id);
                                            const versionGroup = bot.config?.versionGroup;
                                            const isExpanded = expandedBots[versionGroup || bot.id] || false;

                                            let availableVersions = [];
                                            let botsInGroup = [];

                                            if (versionGroup && botsByVersionGroup[versionGroup]) {
                                                botsInGroup = botsByVersionGroup[versionGroup];
                                                availableVersions = botsInGroup
                                                    .filter(b => b?.status !== 'DRAFT')
                                                    .map(b => b.config?.version)
                                                    .filter(Boolean);
                                            }

                                            return (
                                                <BotBlockWrapper
                                                    key={bot.id}
                                                    data={bot}
                                                    botId={bot.id}
                                                    isExpanded={isExpanded}
                                                    isSelected={isSelected}
                                                    onVersionChange={handleVersionChange}
                                                    onToggleExpanded={handleToggleExpanded}
                                                    onToggleSelection={toggleBotSelection}
                                                    availableVersions={availableVersions}
                                                />
                                            );
                                        }) :
                                        <p className={"placeholder"}>
                                            {intl.formatMessage({
                                                id: 'noBotModulesPublished',
                                                defaultMessage: 'No support bot modules have been published yet.'
                                            })}
                                        </p>
                                    }
                                </div>
                                <button
                                    className="custom-instructions-button"
                                    onClick={handleToggleCustomInstructions}
                                >
                                    <TestTubeDiagonal size={16} />
                                    {intl.formatMessage({
                                        id: 'useCustomInstructions',
                                        defaultMessage: 'Bypass modules with custom instructions'
                                    })}
                                </button>
                            </> :
                            <>
                                <div className={"section-header"}>
                                    <h4>
                                        <TestTubeDiagonal size={18} />
                                        {intl.formatMessage({
                                            id: 'botInstructions',
                                            defaultMessage: 'Bot Instructions',
                                        })}
                                    </h4>
                                </div>
                                <div className={"custom-instructions"}>
                                    <textarea
                                        value={customInstructions}
                                        onChange={(e) => handleCustomInstructionsChange(e.target.value)}
                                        placeholder={customInstructionsPlaceholder}
                                        className={"custom-instructions-textarea"}
                                        autoFocus={true}
                                    />
                                    <button
                                        className="custom-instructions-button"
                                        onClick={handleToggleCustomInstructions}
                                    >
                                        <BackArrow />
                                        {intl.formatMessage({
                                            id: 'useBotModules',
                                            defaultMessage: 'Use support bot modules'
                                        })}
                                    </button>
                                </div>
                            </>
                    }
                </div>
            </div>
        </>
    );
};

function BotBlockWrapper({
                      data,
                      botId,
                      isExpanded,
                      onVersionChange,
                      onToggleExpanded,
                      isSelected,
                      onToggleSelection,
                      availableVersions
                  }) {
    const { value: userData } = useDataContext('userData');
    const { value: categoryData } = useDataContext('categoryData');

    return (
        <BotCard
            data={data}
            botId={botId}
            mode="select"
            isExpanded={isExpanded}
            isSelected={isSelected}
            onToggleExpanded={onToggleExpanded}
            onVersionChange={onVersionChange}
            onToggleSelection={onToggleSelection}
            availableVersions={availableVersions}
            userDataSource={userData}
            topicDataSource={categoryData}
        />
    );
}

export default SurveyBots;